import { useEffect, useState } from "react";

// Build-time hard override - baked into the bundle by CRA at `npm run build`.
// Changing this in Amplify Console requires a manual redeploy to take effect
// (see MAINTENANCE_MODE_SETUP.md).
export const MAINTENANCE_ENV_OVERRIDE = process.env.REACT_APP_MAINTENANCE_MODE === "true";

// Local-dev escape hatch - skips the remote check entirely so a developer's
// machine never gets blocked by the shared production flag. Set in a
// gitignored .env.local (never committed, never deployed), so it has zero
// effect on production builds.
export const MAINTENANCE_CHECK_DISABLED = process.env.REACT_APP_SKIP_MAINTENANCE_CHECK === "true";

// Public, non-secret S3 object polled at runtime - lets maintenance mode be
// flipped on/off from the AWS console (even from a phone) with no rebuild.
// Must be the region-specific endpoint (bucket lives in eu-central-1) - the
// generic `s3.amazonaws.com` host 307-redirects to it, and that redirect
// response carries no CORS headers, which the browser blocks before it ever
// reaches the real object.
export const MAINTENANCE_FLAG_URL =
  "https://sibolab-maintenance-flag.s3.eu-central-1.amazonaws.com/maintenance.json";

export const MAINTENANCE_POLL_INTERVAL_MS = 60_000;

// App-wide maintenance gate. Returns "checking" | "maintenance" | "normal".
//
// Starts at "checking" (unless the build-time override is on, which skips
// straight to "maintenance" and never fetches) so the router/auth tree never
// mounts - and no API call can fire - before we've confirmed it's safe to.
// The caller must treat "checking" the same as "maintenance" for gating
// purposes (block routes), just rendered differently (a neutral loading
// state, not the maintenance message, since this is the common fast path).
//
// Any fetch error (offline, CORS, bucket down, bad JSON) fails open to
// "normal" ONLY from "checking" - it never overwrites an already-confirmed
// "maintenance" from a prior successful poll, so a transient blip can't
// silently let users back in once maintenance has been genuinely confirmed.
export function useMaintenanceMode() {
  const [status, setStatus] = useState(() => {
    if (MAINTENANCE_CHECK_DISABLED) return "normal";
    return MAINTENANCE_ENV_OVERRIDE ? "maintenance" : "checking";
  });

  useEffect(() => {
    if (MAINTENANCE_CHECK_DISABLED || MAINTENANCE_ENV_OVERRIDE) return;

    let cancelled = false;

    async function checkRemoteFlag() {
      try {
        const res = await fetch(`${MAINTENANCE_FLAG_URL}?t=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = await res.json();
        if (!cancelled) setStatus(data?.maintenance === true ? "maintenance" : "normal");
      } catch (err) {
        console.warn("Maintenance flag check failed:", err);
        if (!cancelled) setStatus((prev) => (prev === "checking" ? "normal" : prev));
      }
    }

    checkRemoteFlag();
    const id = setInterval(checkRemoteFlag, MAINTENANCE_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return status;
}
